import { useState, useEffect, useMemo } from "react";
import { calculateCVCropPoints } from "../utils/cvCropCalculation";

export type { Point } from "../utils/cvCropCalculation";
import type { Point } from "../utils/cvCropCalculation";

interface CropDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
}

function convertPercentagesToPixels(
  points: Point[],
  imageWidth: number,
  imageHeight: number,
): CropDimensions | null {
  if (!points || points.length === 0) return null;

  const xCoords = points.map((p: Point) => p[0]);
  const yCoords = points.map((p: Point) => p[1]);

  const minX_percent = Math.min(...xCoords);
  const maxX_percent = Math.max(...xCoords);
  const minY_percent = Math.min(...yCoords);
  const maxY_percent = Math.max(...yCoords);

  return {
    x: Math.round(minX_percent * imageWidth),
    y: Math.round(minY_percent * imageHeight),
    width: Math.round((maxX_percent - minX_percent) * imageWidth),
    height: Math.round((maxY_percent - minY_percent) * imageHeight),
  };
}

interface CropResult {
  croppedImageUrl: string | null;
  cropDimensions: CropDimensions | null;
  originalDimensions: { width: number; height: number } | null;
  cropPoints: Point[] | null;
  crop?: boolean;
}

// Module-level cache so repeated mounts with the same inputs skip the async
// canvas pipeline entirely. Capped to avoid unbounded memory growth.
const MAX_CACHE_SIZE = 30;
const cropResultCache = new Map<string, CropResult>();

function setCacheEntry(key: string, result: CropResult) {
  if (cropResultCache.size >= MAX_CACHE_SIZE) {
    cropResultCache.delete(cropResultCache.keys().next().value as string);
  }
  cropResultCache.set(key, result);
}

export function useImageCrop(
  imageSrc: string | undefined,
  points: { points: Point[] }[],
  cropPoints?: Point[],
  crop: boolean = false,
): CropResult {
  const cropPointsKey = useMemo(() => JSON.stringify(cropPoints), [cropPoints]);
  const pointsKey = useMemo(() => JSON.stringify(points), [points]);

  const cacheKey = imageSrc
    ? `${imageSrc}||${pointsKey}||${cropPointsKey}||${crop}`
    : "";

  const [cropResult, setCropResult] = useState<CropResult>(() => {
    // Synchronous cache hit on first render — no loading flash for repeated mounts.
    if (cacheKey && cropResultCache.has(cacheKey)) {
      return cropResultCache.get(cacheKey)!;
    }
    return {
      croppedImageUrl: null,
      cropDimensions: null,
      originalDimensions: null,
      cropPoints: null,
    };
  });

  useEffect(() => {
    if (!imageSrc) return;

    // Cache hit: call setCropResult so a reconciled instance (same component
    // reused at the same tree position with different src/points props) also
    // updates. React bails out with no extra render when the reference is
    // already identical (Object.is), so initial-render cache hits are free.
    if (cacheKey && cropResultCache.has(cacheKey)) {
      setCropResult(cropResultCache.get(cacheKey)!);
      return;
    }

    setCropResult({
      croppedImageUrl: null,
      cropDimensions: null,
      originalDimensions: null,
      cropPoints: null,
    });

    let cancelled = false;

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;

    image.onload = () => {
      if (cancelled) return;

      const originalDimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };

      const cropSize = crop ? 2 : 1;
      const calculatedCropPoints = calculateCVCropPoints(
        points,
        originalDimensions,
        cropSize,
      );

      const pixelCrop = convertPercentagesToPixels(
        cropPoints || calculatedCropPoints || [],
        image.naturalWidth,
        image.naturalHeight,
      );

      if (!pixelCrop || pixelCrop.width === 0 || pixelCrop.height === 0) {
        console.error("Invalid crop dimensions calculated.");
        return;
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );

      if (cancelled) return;

      const result: CropResult = {
        croppedImageUrl: canvas.toDataURL("image/jpeg"),
        cropDimensions: pixelCrop,
        originalDimensions,
        cropPoints: cropPoints || calculatedCropPoints,
      };

      if (cacheKey) setCacheEntry(cacheKey, result);
      setCropResult(result);
    };

    image.onerror = () => {
      if (cancelled) return;
      console.error("Failed to load image for cropping.");
      setCropResult({
        croppedImageUrl: null,
        cropDimensions: null,
        originalDimensions: null,
        cropPoints: null,
      });
    };

    return () => {
      cancelled = true;
    };
  }, [imageSrc, crop, cropPointsKey, pointsKey, cacheKey]);

  return cropResult;
}
